import { useState, useMemo, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Grid, List, Pencil } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Workout, ExerciseBlock } from '@/types/workout';
import { ExerciseType } from '@/types/exercise';
import { ExerciseListItem } from './ExerciseListItem';
import { AddExerciseDialog } from './AddExerciseDialog';
import { CompactWorkoutTable } from './CompactWorkoutTable';
import { Button } from '@/components/ui/button';
import { getSwapAlternatives } from '@/services/aiService';
import { useWorkoutStore } from '@/lib/stores/workoutStore';
import { workoutApi } from '@/services/workoutApi';
import { loadWorkoutViewMode, saveWorkoutViewMode } from '@/lib/storage';

type WorkoutSource = 'generate' | 'quick' | 'list';

interface WorkoutOverviewProps {
  workout: Workout;
  onStart: (workout: Workout) => void;
  onBack: () => void;
  onSaveOnly?: (workout: Workout) => void;
  overviewSource?: WorkoutSource;
}

function SortableExerciseItem({
  exercise,
  index,
  onUpdate,
  onSwap,
  onToggleSuperset,
  isSuperset,
  onDelete,
  supersetGroupId,
  supersetGroupIndex,
  availableSupersetGroups,
  workoutId,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  isFirstInGroup,
  isLastInGroup,
}: {
  exercise: ExerciseBlock;
  index: number;
  onUpdate: (updates: Partial<ExerciseBlock>) => void;
  onSwap: () => void;
  onToggleSuperset: (groupId?: string) => void;
  isSuperset: boolean;
  onDelete: () => void;
  supersetGroupId?: string;
  supersetGroupIndex?: number;
  availableSupersetGroups?: Array<{ id: string; letter: string; exerciseCount: number }>;
  workoutId?: string;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}) {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ 
    id: exercise.id,
    // Only enable drag for superset items (internal reorder)
    disabled: !isSuperset,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Only pass drag handle props for superset items
  const dragHandleProps = isSuperset ? { attributes, listeners } : undefined;

  return (
    <div ref={setNodeRef} style={style}>
      <ExerciseListItem
        exercise={exercise}
        index={index}
        onUpdate={onUpdate}
        onSwap={onSwap}
        onToggleSuperset={onToggleSuperset}
        isSuperset={isSuperset}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        onDelete={onDelete}
        supersetGroupId={supersetGroupId}
        supersetGroupIndex={supersetGroupIndex}
        availableSupersetGroups={availableSupersetGroups}
        workoutId={workoutId}
        dragHandleProps={dragHandleProps}
        isDragging={isDragging}
        isFirstInGroup={isFirstInGroup}
        isLastInGroup={isLastInGroup}
      />
    </div>
  );
}

export function WorkoutOverview({ workout, onStart, onBack, onSaveOnly, overviewSource = 'list' }: WorkoutOverviewProps) {
  const [exercises, setExercises] = useState<ExerciseBlock[]>(workout.exercises);
  const [warmupCollapsed, setWarmupCollapsed] = useState(false);
  const [cooldownCollapsed, setCooldownCollapsed] = useState(false);
  const [swapExerciseId, setSwapExerciseId] = useState<string | null>(null);
  const [swapAlternatives, setSwapAlternatives] = useState<ExerciseBlock[]>([]);
  const [showAddExerciseDialog, setShowAddExerciseDialog] = useState(false);
  const [deleteConfirmExerciseId, setDeleteConfirmExerciseId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'detailed' | 'compact'>('compact');
  const workoutRef = useRef(workout);
  const isNewWorkout = overviewSource === 'generate' || overviewSource === 'quick';
  // For new workouts from generate/quick flow, assume not saved yet (even if they have a client-generated ID)
  const hasBeenSavedRef = useRef(!isNewWorkout && workout.id && !workout.is_preset);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Categorize exercises by phase (warmup/cooldown/main based on phase field)
  const warmupExercises = useMemo(() => {
    return exercises.filter(
      (exercise) => exercise.phase === 'warmup'
    );
  }, [exercises]);

  const cooldownExercises = useMemo(() => {
    return exercises.filter(
      (exercise) => exercise.phase === 'cooldown'
    );
  }, [exercises]);

  const mainExercises = useMemo(() => {
    // All exercises that are not warmup or cooldown (default to main)
    return exercises.filter(
      (exercise) => !exercise.phase || exercise.phase === 'main'
    );
  }, [exercises]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setExercises((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      
      const draggedItem = items[oldIndex];
      const targetItem = items[newIndex];
      
      // Only allow drag within superset groups
      if (draggedItem?.superset_group_id) {
        // Dragged item is in a superset - only allow drop within same group
        if (targetItem?.superset_group_id !== draggedItem.superset_group_id) {
          // Drop target is not in the same group - reject
          return items;
        }
      } else {
        // Non-superset items cannot be dragged (they use Up/Down buttons)
        return items;
      }
      
      const newItems = arrayMove(items, oldIndex, newIndex);
      
      // Update order values
      const reordered = newItems.map((item, index) => ({ ...item, order: index }));
      // Save immediately for new workouts
      if (isNewWorkout) {
        saveWorkout(reordered);
      }
      return reordered;
    });
  };

  // Helper: Find the contiguous block containing an exercise (for superset-aware moves)
  const getExerciseBlock = (exerciseId: string, items: ExerciseBlock[]) => {
    const exercise = items.find(e => e.id === exerciseId);
    const exerciseIndex = items.findIndex(e => e.id === exerciseId);
    
    if (!exercise?.superset_group_id) {
      // Single exercise block
      return { start: exerciseIndex, end: exerciseIndex, groupId: null };
    }
    
    // Find contiguous range of this superset
    const groupId = exercise.superset_group_id;
    let start = exerciseIndex;
    let end = exerciseIndex;
    
    // Expand backwards
    while (start > 0 && items[start - 1].superset_group_id === groupId) {
      start--;
    }
    // Expand forwards
    while (end < items.length - 1 && items[end + 1].superset_group_id === groupId) {
      end++;
    }
    
    return { start, end, groupId };
  };

  // Helper: Get the previous block (for swap)
  const getPreviousBlock = (currentStart: number, items: ExerciseBlock[]) => {
    if (currentStart === 0) return null;
    
    const prevExercise = items[currentStart - 1];
    if (!prevExercise.superset_group_id) {
      return { start: currentStart - 1, end: currentStart - 1 };
    }
    
    // Find the start of the previous superset block
    const groupId = prevExercise.superset_group_id;
    let start = currentStart - 1;
    while (start > 0 && items[start - 1].superset_group_id === groupId) {
      start--;
    }
    return { start, end: currentStart - 1 };
  };

  // Helper: Get the next block (for swap)
  const getNextBlock = (currentEnd: number, items: ExerciseBlock[]) => {
    if (currentEnd >= items.length - 1) return null;
    
    const nextExercise = items[currentEnd + 1];
    if (!nextExercise.superset_group_id) {
      return { start: currentEnd + 1, end: currentEnd + 1 };
    }
    
    // Find the end of the next superset block
    const groupId = nextExercise.superset_group_id;
    let end = currentEnd + 1;
    while (end < items.length - 1 && items[end + 1].superset_group_id === groupId) {
      end++;
    }
    return { start: currentEnd + 1, end };
  };

  // Block-aware move: moves entire superset groups together
  const moveExercise = (exerciseId: string, direction: 'up' | 'down') => {
    setExercises((items) => {
      const exercise = items.find(e => e.id === exerciseId);
      if (!exercise) return items;
      
      // Find current block
      const currentBlock = getExerciseBlock(exerciseId, items);
      
      let reordered: ExerciseBlock[];
      
      if (direction === 'up') {
        const prevBlock = getPreviousBlock(currentBlock.start, items);
        if (!prevBlock) return items;
        
        // Swap blocks: [prevBlock][currentBlock] -> [currentBlock][prevBlock]
        const before = items.slice(0, prevBlock.start);
        const prev = items.slice(prevBlock.start, prevBlock.end + 1);
        const current = items.slice(currentBlock.start, currentBlock.end + 1);
        const after = items.slice(currentBlock.end + 1);
        
        reordered = [...before, ...current, ...prev, ...after].map((item, i) => ({ ...item, order: i }));
      } else {
        const nextBlock = getNextBlock(currentBlock.end, items);
        if (!nextBlock) return items;
        
        // Swap blocks: [currentBlock][nextBlock] -> [nextBlock][currentBlock]
        const before = items.slice(0, currentBlock.start);
        const current = items.slice(currentBlock.start, currentBlock.end + 1);
        const next = items.slice(nextBlock.start, nextBlock.end + 1);
        const after = items.slice(nextBlock.end + 1);
        
        reordered = [...before, ...next, ...current, ...after].map((item, i) => ({ ...item, order: i }));
      }
      
      // Save immediately for new workouts
      if (isNewWorkout) {
        saveWorkout(reordered);
      }
      return reordered;
    });
  };

  const handleUpdateExercise = (exerciseId: string, updates: Partial<ExerciseBlock>) => {
    setExercises((items) => {
      const updated = items.map((item) => (item.id === exerciseId ? { ...item, ...updates } : item));
      // Save immediately for new workouts
      if (isNewWorkout) {
        saveWorkout(updated);
      }
      return updated;
    });
  };

  const handleSwapClick = async (exerciseId: string) => {
    const updatedWorkout = { ...workout, exercises };
    const alternatives = await getSwapAlternatives(exerciseId, updatedWorkout);
    setSwapExerciseId(exerciseId);
    setSwapAlternatives(alternatives);
  };

  const handleSwapSelect = (alternative: ExerciseBlock) => {
    if (!swapExerciseId) return;
    
    setExercises((items) => {
      const updated = items.map((item) => (item.id === swapExerciseId ? alternative : item));
      // Save immediately for new workouts
      if (isNewWorkout) {
        saveWorkout(updated);
      }
      return updated;
    });
    setSwapExerciseId(null);
    setSwapAlternatives([]);
  };

  const handleToggleSuperset = (exerciseId: string, groupId?: string) => {
    setExercises((items) => {
      const updated = items.map((item) => {
        if (item.id === exerciseId) {
          if (groupId) {
            // Add to specific group
            return { ...item, superset_group_id: groupId };
          } else if (item.superset_group_id) {
            // Remove from superset
            return { ...item, superset_group_id: undefined };
          } else {
            // Create new group
            const newGroupId = `superset-${Date.now()}`;
            return { ...item, superset_group_id: newGroupId };
          }
        }
        return item;
      });
      // Save immediately for new workouts
      if (isNewWorkout) {
        saveWorkout(updated);
      }
      return updated;
    });
  };

  const getSupersetGroup = (exerciseId: string) => {
    const exercise = exercises.find((e) => e.id === exerciseId);
    return exercise?.superset_group_id;
  };

  // Get all unique superset groups and their exercises
  const supersetGroups = useMemo(() => {
    const groups: Map<string, { exercises: ExerciseBlock[]; index: number }> = new Map();
    let groupIndex = 0;
    
    exercises.forEach((exercise) => {
      if (exercise.superset_group_id) {
        if (!groups.has(exercise.superset_group_id)) {
          groups.set(exercise.superset_group_id, { exercises: [], index: groupIndex++ });
        }
        groups.get(exercise.superset_group_id)!.exercises.push(exercise);
      }
    });
    
    return groups;
  }, [exercises]);

  const getSupersetGroupInfo = (exerciseId: string) => {
    const exercise = exercises.find((e) => e.id === exerciseId);
    if (!exercise?.superset_group_id) return null;
    
    const group = supersetGroups.get(exercise.superset_group_id);
    if (!group) return null;
    
    return {
      id: exercise.superset_group_id,
      index: group.index,
    };
  };

  // Check if an exercise is the first or last in its superset group (for visual styling)
  const getGroupPosition = (exerciseId: string) => {
    const exercise = exercises.find((e) => e.id === exerciseId);
    if (!exercise?.superset_group_id) {
      return { isFirstInGroup: false, isLastInGroup: false };
    }
    
    const exerciseIndex = exercises.findIndex(e => e.id === exerciseId);
    const groupId = exercise.superset_group_id;
    
    // Check if this is the first exercise in the group (no previous item with same groupId)
    const isFirstInGroup = exerciseIndex === 0 || 
      exercises[exerciseIndex - 1].superset_group_id !== groupId;
    
    // Check if this is the last exercise in the group (no next item with same groupId)
    const isLastInGroup = exerciseIndex === exercises.length - 1 || 
      exercises[exerciseIndex + 1].superset_group_id !== groupId;
    
    return { isFirstInGroup, isLastInGroup };
  };

  // Get available superset groups for selector (excluding current exercise's group)
  const getAvailableSupersetGroups = (currentExerciseId: string) => {
    const currentExercise = exercises.find((e) => e.id === currentExerciseId);
    const availableGroups: Array<{ id: string; letter: string; exerciseCount: number }> = [];
    
    supersetGroups.forEach((group, groupId) => {
      // Exclude the group this exercise is already in
      if (currentExercise?.superset_group_id !== groupId) {
        const letter = String.fromCharCode(65 + group.index);
        availableGroups.push({
          id: groupId,
          letter,
          exerciseCount: group.exercises.length,
        });
      }
    });
    
    return availableGroups;
  };

  // Save workout immediately for new workouts
  const saveWorkout = async (updatedExercises: ExerciseBlock[]) => {
    if (!isNewWorkout || workout.is_preset) {
      return; // Don't save for existing workouts or presets
    }

    if (isSaving) return; // Prevent concurrent saves
    
    setIsSaving(true);
    try {
      const updatedWorkout = { ...workoutRef.current, exercises: updatedExercises };
      
      if (!hasBeenSavedRef.current) {
        // First time saving - create workout
        const createdWorkout = await workoutApi.createWorkout(updatedWorkout);
        workoutRef.current = createdWorkout;
        hasBeenSavedRef.current = true;
        // Update store
        const { addGeneratedWorkout } = useWorkoutStore.getState();
        await addGeneratedWorkout(createdWorkout);
      } else {
        // Update existing workout
        const { updateWorkout } = useWorkoutStore.getState();
        await updateWorkout(updatedWorkout);
        workoutRef.current = updatedWorkout;
      }
    } catch (error) {
      console.error('Failed to save workout:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Save existing workout (not a new workout)
  const saveExistingWorkout = async (updatedExercises: ExerciseBlock[]) => {
    if (!workout.id || workout.is_preset || isNewWorkout) {
      return; // Only save existing, non-preset workouts
    }

    if (isSaving) return; // Prevent concurrent saves
    
    setIsSaving(true);
    try {
      const updatedWorkout = { ...workout, exercises: updatedExercises };
      const { updateWorkout } = useWorkoutStore.getState();
      await updateWorkout(updatedWorkout);
    } catch (error) {
      console.error('Failed to save existing workout:', error);
      throw error; // Re-throw so caller can handle
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteExercise = (exerciseId: string) => {
    setDeleteConfirmExerciseId(exerciseId);
  };

  const confirmDeleteExercise = () => {
    if (!deleteConfirmExerciseId) return;
    
    setExercises((items) => {
      const filtered = items.filter((item) => item.id !== deleteConfirmExerciseId);
      // Update order values after deletion
      const reordered = filtered.map((item, index) => ({ ...item, order: index }));
      // Save immediately for new workouts
      if (isNewWorkout) {
        saveWorkout(reordered);
      }
      return reordered;
    });
    
    setDeleteConfirmExerciseId(null);
  };

  const handleAddExercise = (newExercise: ExerciseBlock) => {
    // Determine which section to add to based on phase (or exercise type as fallback)
    let phase = newExercise.phase;
    
    // If phase not set, infer from exercise type as fallback
    if (!phase) {
      const exerciseType = newExercise.exercise_type;
      if (exerciseType === ExerciseType.WARMUP || exerciseType === ExerciseType.WARM_UP) {
        phase = 'warmup';
      } else if (exerciseType === ExerciseType.COOLDOWN) {
        phase = 'cooldown';
      } else {
        phase = 'main';
      }
    }
    
    // Set the phase on the exercise
    const exerciseWithPhase = { ...newExercise, phase };
    
    setExercises((items) => {
      const newItems = [...items];
      
      if (phase === 'warmup') {
        // Add to beginning (before main exercises)
        const warmupCount = items.filter((e) => e.phase === 'warmup').length;
        newItems.splice(warmupCount, 0, exerciseWithPhase);
      } else if (phase === 'cooldown') {
        // Add to end
        newItems.push(exerciseWithPhase);
      } else {
        // Add to main section (after warmup, before cooldown)
        const warmupCount = items.filter((e) => e.phase === 'warmup').length;
        const cooldownCount = items.filter((e) => e.phase === 'cooldown').length;
        const mainCount = items.length - warmupCount - cooldownCount;
        newItems.splice(warmupCount + mainCount, 0, exerciseWithPhase);
      }
      
      // Update order values
      const reordered = newItems.map((item, index) => ({ ...item, order: index }));
      // Save immediately for new workouts
      if (isNewWorkout) {
        saveWorkout(reordered);
      }
      return reordered;
    });
    
    setShowAddExerciseDialog(false);
  };

  const handleStart = async () => {
    // If in edit mode (detailed view), save and switch back to compact mode
    if (viewMode === 'detailed') {
      try {
        if (isNewWorkout) {
          // Save new workout
          await saveWorkout(exercises);
        } else {
          // Save existing workout
          await saveExistingWorkout(exercises);
        }
        // Refresh workout list to ensure it's up to date
        const { refreshWorkouts } = useWorkoutStore.getState();
        await refreshWorkouts();
        // Switch back to compact mode (stays in workout overview)
        setViewMode('compact');
      } catch (error) {
        console.error('Failed to save workout:', error);
        // Don't switch mode if save failed
      }
      return;
    }

    // Compact view: start workout (existing behavior)
    // Use the latest workout ref for new workouts, or current workout for existing
    const updatedWorkout = isNewWorkout && workoutRef.current.id 
      ? { ...workoutRef.current, exercises }
      : { ...workout, exercises };
    
    // For existing workouts, we don't save - just pass modified workout to session
    // For new workouts, ensure it's saved before starting
    if (isNewWorkout && !hasBeenSavedRef.current) {
      await saveWorkout(exercises);
      if (workoutRef.current.id) {
        onStart({ ...workoutRef.current, exercises });
        return;
      }
    }
    
    onStart(updatedWorkout);
  };

  // Calculate total duration from exercises
  const calculateTotalDuration = useMemo(() => {
    return exercises.reduce((total, exercise) => {
      let exerciseTime = 0;
      
      if (exercise.target.mode === 'time') {
        exerciseTime = exercise.target.duration_seconds || 0;
      } else {
        // Estimate for reps-based: 5 seconds per rep × sets × reps
        const sets = exercise.target.sets || 1;
        const reps = exercise.target.reps || 10;
        exerciseTime = 5 * sets * reps;
      }
      
      // Add rest time
      return total + exerciseTime + (exercise.rest_seconds || 0);
    }, 0);
  }, [exercises]);

  const totalDurationMinutes = Math.round(calculateTotalDuration / 60);
  const exerciseCount = exercises.length;

  // Update workout ref when workout prop changes
  useEffect(() => {
    workoutRef.current = workout;
    if (workout.id && !workout.is_preset) {
      hasBeenSavedRef.current = true;
    }
  }, [workout]);

  // Persist view mode changes to localStorage
  useEffect(() => {
    saveWorkoutViewMode(viewMode);
  }, [viewMode]);

  const handleToggleViewMode = () => {
    setViewMode((prev) => (prev === 'detailed' ? 'compact' : 'detailed'));
  };

  return (
    <div className="min-h-screen bg-neutral-50 pb-24">
      <div className="max-w-2xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
          
          <h1 className="text-4xl font-bold mb-2 text-neutral-900">{workout.name}</h1>
          <p className="text-lg text-neutral-600 mb-2">{workout.description}</p>
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-neutral-500">
              <span>{totalDurationMinutes} min · {exerciseCount} {exerciseCount === 1 ? 'exercise' : 'exercises'}</span>
            </div>
            {viewMode === 'compact' && (
              <button
                onClick={handleToggleViewMode}
                className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>

        {/* Exercises */}
        {viewMode === 'compact' ? (
          <CompactWorkoutTable 
            exercises={exercises} 
            isPreset={workout.is_preset}
            onExerciseUpdate={handleUpdateExercise}
            workoutId={workout.id}
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={exercises.map((e) => e.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-0">
                {/* Warmup Section */}
                {warmupExercises.length > 0 && (
                <div>
                  <button
                    onClick={() => setWarmupCollapsed(!warmupCollapsed)}
                    className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-3 transition-colors"
                  >
                    {warmupCollapsed ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronUp className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium">Warmup</span>
                  </button>
                  {!warmupCollapsed && (
                    <div className="space-y-0">
                      {warmupExercises.map((exercise, index) => {
                        const groupInfo = getSupersetGroupInfo(exercise.id);
                        const block = getExerciseBlock(exercise.id, exercises);
                        const { isFirstInGroup, isLastInGroup } = getGroupPosition(exercise.id);
                        return (
                          <div key={exercise.id}>
                            <SortableExerciseItem
                              exercise={exercise}
                              index={index}
                              onUpdate={(updates) => handleUpdateExercise(exercise.id, updates)}
                              onSwap={() => handleSwapClick(exercise.id)}
                              onToggleSuperset={(groupId) => handleToggleSuperset(exercise.id, groupId)}
                              isSuperset={!!getSupersetGroup(exercise.id)}
                              onDelete={() => handleDeleteExercise(exercise.id)}
                              supersetGroupId={exercise.superset_group_id}
                              supersetGroupIndex={groupInfo?.index}
                              availableSupersetGroups={getAvailableSupersetGroups(exercise.id)}
                              workoutId={workout.id}
                              onMoveUp={() => moveExercise(exercise.id, 'up')}
                              onMoveDown={() => moveExercise(exercise.id, 'down')}
                              canMoveUp={block.start > 0}
                              canMoveDown={block.end < exercises.length - 1}
                              isFirstInGroup={isFirstInGroup}
                              isLastInGroup={isLastInGroup}
                            />
                            {index < warmupExercises.length - 1 && !workout.is_preset && (
                              <div className="relative my-3">
                                <div className="absolute inset-0 flex items-center">
                                  <div className="w-full border-t border-neutral-200"></div>
                                </div>
                                <div className="relative flex justify-center">
                                  <button
                                    onClick={() => setShowAddExerciseDialog(true)}
                                    className="bg-white w-7 h-7 rounded-full border border-dashed border-neutral-200 hover:border-neutral-300 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition-colors"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Separator between Warmup and Main */}
              {warmupExercises.length > 0 && mainExercises.length > 0 && !workout.is_preset && (
                <div className="relative my-3">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-200"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <button
                      onClick={() => setShowAddExerciseDialog(true)}
                      className="bg-neutral-50 w-7 h-7 rounded-full border border-dashed border-neutral-200 hover:border-neutral-300 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Main Exercises */}
              {mainExercises.length > 0 && (
                <div className="space-y-0">
                  {mainExercises.map((exercise, index) => {
                    const groupInfo = getSupersetGroupInfo(exercise.id);
                    const block = getExerciseBlock(exercise.id, exercises);
                    const { isFirstInGroup, isLastInGroup } = getGroupPosition(exercise.id);
                    return (
                      <div key={exercise.id}>
                        <SortableExerciseItem
                          exercise={exercise}
                          index={warmupExercises.length + index}
                          onUpdate={(updates) => handleUpdateExercise(exercise.id, updates)}
                          onSwap={() => handleSwapClick(exercise.id)}
                          onToggleSuperset={(groupId) => handleToggleSuperset(exercise.id, groupId)}
                          isSuperset={!!getSupersetGroup(exercise.id)}
                          onDelete={() => handleDeleteExercise(exercise.id)}
                          supersetGroupId={exercise.superset_group_id}
                          supersetGroupIndex={groupInfo?.index}
                          availableSupersetGroups={getAvailableSupersetGroups(exercise.id)}
                          onMoveUp={() => moveExercise(exercise.id, 'up')}
                          onMoveDown={() => moveExercise(exercise.id, 'down')}
                          canMoveUp={block.start > 0}
                          canMoveDown={block.end < exercises.length - 1}
                          isFirstInGroup={isFirstInGroup}
                          isLastInGroup={isLastInGroup}
                        />
                        {index < mainExercises.length - 1 && !workout.is_preset && (
                          <div className="relative my-3">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-neutral-200"></div>
                            </div>
                            <div className="relative flex justify-center">
                              <button
                                onClick={() => setShowAddExerciseDialog(true)}
                                className="bg-white w-8 h-8 rounded-full border-2 border-dashed border-neutral-300 hover:border-neutral-400 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Separator between Main and Cooldown */}
              {mainExercises.length > 0 && cooldownExercises.length > 0 && !workout.is_preset && (
                <div className="relative my-3">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-200"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <button
                      onClick={() => setShowAddExerciseDialog(true)}
                      className="bg-neutral-50 w-7 h-7 rounded-full border border-dashed border-neutral-200 hover:border-neutral-300 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Cooldown Section */}
              {cooldownExercises.length > 0 && (
                <div>
                  <button
                    onClick={() => setCooldownCollapsed(!cooldownCollapsed)}
                    className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-3 transition-colors"
                  >
                    {cooldownCollapsed ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronUp className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium">Cooldown</span>
                  </button>
                  {!cooldownCollapsed && (
                    <div className="space-y-0">
                      {cooldownExercises.map((exercise, index) => {
                        const groupInfo = getSupersetGroupInfo(exercise.id);
                        const block = getExerciseBlock(exercise.id, exercises);
                        const { isFirstInGroup, isLastInGroup } = getGroupPosition(exercise.id);
                        return (
                          <div key={exercise.id}>
                            <SortableExerciseItem
                              exercise={exercise}
                              index={exercises.length - cooldownExercises.length + index}
                              onUpdate={(updates) => handleUpdateExercise(exercise.id, updates)}
                              onSwap={() => handleSwapClick(exercise.id)}
                              onToggleSuperset={(groupId) => handleToggleSuperset(exercise.id, groupId)}
                              isSuperset={!!getSupersetGroup(exercise.id)}
                              onDelete={() => handleDeleteExercise(exercise.id)}
                              supersetGroupId={exercise.superset_group_id}
                              supersetGroupIndex={groupInfo?.index}
                              availableSupersetGroups={getAvailableSupersetGroups(exercise.id)}
                              onMoveUp={() => moveExercise(exercise.id, 'up')}
                              onMoveDown={() => moveExercise(exercise.id, 'down')}
                              canMoveUp={block.start > 0}
                              canMoveDown={block.end < exercises.length - 1}
                              isFirstInGroup={isFirstInGroup}
                              isLastInGroup={isLastInGroup}
                            />
                            {index < cooldownExercises.length - 1 && !workout.is_preset && (
                              <div className="relative my-3">
                                <div className="absolute inset-0 flex items-center">
                                  <div className="w-full border-t border-neutral-200"></div>
                                </div>
                                <div className="relative flex justify-center">
                                  <button
                                    onClick={() => setShowAddExerciseDialog(true)}
                                    className="bg-white w-7 h-7 rounded-full border border-dashed border-neutral-200 hover:border-neutral-300 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition-colors"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
        )}

        {/* Swap Modal */}
        {swapExerciseId && swapAlternatives.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-xl font-semibold mb-4">Swap Exercise</h3>
              <div className="space-y-3">
                {swapAlternatives.map((alternative) => (
                  <button
                    key={alternative.id}
                    onClick={() => handleSwapSelect(alternative)}
                    className="w-full text-left p-4 border border-neutral-200 rounded-lg hover:border-neutral-900 hover:bg-neutral-50 transition-all"
                  >
                    <div className="font-medium text-neutral-900">{alternative.name}</div>
                    <div className="text-sm text-neutral-600">{alternative.instruction}</div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setSwapExerciseId(null);
                  setSwapAlternatives([]);
                }}
                className="mt-4 text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmExerciseId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-xl font-semibold mb-2">Remove Exercise</h3>
              <p className="text-neutral-600 mb-6">
                Are you sure you want to remove this exercise from the workout?
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  onClick={() => setDeleteConfirmExerciseId(null)}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDeleteExercise}
                  variant="default"
                  size="sm"
                >
                  Remove
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add Exercise Dialog */}
        <AddExerciseDialog
          isOpen={showAddExerciseDialog}
          onClose={() => setShowAddExerciseDialog(false)}
          onAdd={handleAddExercise}
          currentExerciseCount={exercises.length}
        />

      </div>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-neutral-200 px-6 py-5 safe-bottom z-10">
        <div className="max-w-2xl mx-auto">
          {/* Show two buttons for new workouts from generate flow */}
          {isNewWorkout && viewMode === 'compact' && onSaveOnly ? (
            <div className="flex gap-3">
              <motion.button
                onClick={async () => {
                  // Save workout without starting
                  const updatedWorkout = workoutRef.current.id 
                    ? { ...workoutRef.current, exercises }
                    : { ...workout, exercises };
                  
                  if (!hasBeenSavedRef.current) {
                    await saveWorkout(exercises);
                  }
                  
                  // Note: handleOverviewSaveOnly will refresh the workout list
                  onSaveOnly(workoutRef.current.id ? { ...workoutRef.current, exercises } : updatedWorkout);
                }}
                className="flex-1 bg-white border-2 border-neutral-900 text-neutral-900 rounded-xl px-6 py-4 text-lg font-medium hover:bg-neutral-50 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Save
              </motion.button>
              <motion.button
                onClick={handleStart}
                className="flex-1 bg-neutral-900 text-white rounded-xl px-6 py-4 text-lg font-medium hover:bg-neutral-800 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Start Workout
              </motion.button>
            </div>
          ) : (
            <motion.button
              onClick={handleStart}
              className="w-full bg-neutral-900 text-white rounded-xl px-8 py-4 text-lg font-medium hover:bg-neutral-800 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              {viewMode === 'detailed' ? 'Save & Close' : 'Start Workout'}
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
}

